import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { useLegalSettings } from '../hooks/useLegalSettings';

const DOMAIN = 'marketplacetvde.pt';

function useCompany() {
  const legal = useLegalSettings() || {};
  return {
    brand: legal.nome_comercial || 'Marketplace TVDE',
    owner: legal.denominacao_social || 'L Vizinha Unipessoal LDA',
    contactEmail: legal.email_geral || 'gestaofrotatvde@gmail.com',
    privacyEmail: legal.email_privacidade || legal.email_geral || 'gestaofrotatvde@gmail.com',
    ...legal,
  };
}

const Container = ({ title, seoTitle, description, path, children, testId }) => {
  useSeo({ title: seoTitle || `${title} – Marketplace TVDE`, description, path });
  const c = useCompany();
  return (
    <div className="min-h-screen bg-white text-slate-700" data-testid={testId}>
      <div className="mx-auto max-w-3xl px-4 py-12 md:px-6">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-2 text-xs font-semibold text-emerald-600 hover:text-emerald-700"
        >
          <ArrowLeft className="h-3 w-3" /> Voltar ao Marketplace
        </Link>
        <h1 className="mb-8 text-3xl font-extrabold text-slate-900 md:text-4xl">{title}</h1>
        <div className="prose-marketplace space-y-5 text-sm leading-relaxed text-slate-600">
          {children}
        </div>
        <p className="mt-10 border-t border-slate-200 pt-4 text-[11px] text-slate-400">
          Última actualização: {new Date().toLocaleDateString('pt-PT')}. © {new Date().getFullYear()}{' '}
          {c.owner}. Marca <em>{c.brand}</em>.
        </p>
      </div>
    </div>
  );
};

// Bloco reutilizável com a identificação legal da empresa.
// Campos ainda não fornecidos são OCULTADOS ao público (nunca "por preencher").
function CompanyBlock() {
  const c = useCompany();
  const sede = [c.morada, c.codigo_postal, c.localidade].filter(Boolean).join(', ');
  const registo = [c.conservatoria, c.matricula].filter(Boolean).join(' · ');
  return (
    <ul className="list-disc space-y-1 pl-5" data-testid="legal-company-block">
      <li>Nome comercial: <strong>{c.brand}</strong></li>
      <li>Denominação social: <strong>{c.owner}</strong></li>
      {c.nipc ? <li>NIPC: <strong>{c.nipc}</strong></li> : null}
      {sede ? <li>Sede: <strong>{sede}</strong></li> : null}
      {registo ? <li>Registo comercial: <strong>{registo}</strong></li> : null}
      {c.capital_social ? <li>Capital social: <strong>{c.capital_social}</strong></li> : null}
      <li>Email: <strong>{c.contactEmail}</strong></li>
      {c.telefone ? <li>Telefone: <strong>{c.telefone}</strong></li> : null}
    </ul>
  );
}

// Texto sobre o estado do registo da marca (condicional ao painel).
function BrandStatus() {
  const c = useCompany();
  const estado = c.marca_estado || 'uso_comercial';
  if (estado === 'registada') {
    return (
      <p>
        A marca <strong>{c.brand}</strong> encontra-se <strong>registada</strong>
        {c.marca_numero ? <> (processo n.º {c.marca_numero})</> : null}
        {c.marca_data_concessao ? <>, concedida em {c.marca_data_concessao}</> : null}. É propriedade
        exclusiva de <strong>{c.owner}</strong>. É proibida a sua reprodução sem autorização escrita prévia.
      </p>
    );
  }
  if (estado === 'pedido_pendente') {
    return (
      <p>
        Foi apresentado <strong>pedido de registo</strong> da marca <strong>{c.brand}</strong>
        {c.marca_numero ? <> (processo n.º {c.marca_numero})</> : null}
        {c.marca_data_pedido ? <>, em {c.marca_data_pedido}</> : null}. A marca é utilizada
        comercialmente por <strong>{c.owner}</strong>.
      </p>
    );
  }
  if (estado === 'nao_registada') {
    return (
      <p>
        A marca <strong>{c.brand}</strong> é utilizada comercialmente por <strong>{c.owner}</strong>.
        O respectivo registo ainda não foi apresentado.
      </p>
    );
  }
  return (
    <p>
      A marca <strong>{c.brand}</strong> é utilizada comercialmente por <strong>{c.owner}</strong>.
    </p>
  );
}

// Secção de Resolução Alternativa de Litígios (configurável no painel).
function RALSection() {
  const c = useCompany();
  const hasRAL = c.ral_nome || c.ral_website || c.ral_email;
  return (
    <>
      <p>
        Em conformidade com a Lei 144/2015, o consumidor pode recorrer a uma entidade de resolução
        alternativa de litígios de consumo (RAL).
      </p>
      {hasRAL ? (
        <ul className="list-disc space-y-1 pl-5">
          {c.ral_nome ? <li>Entidade: <strong>{c.ral_nome}</strong></li> : null}
          {c.ral_morada ? <li>Morada: {c.ral_morada}</li> : null}
          {c.ral_telefone ? <li>Telefone: {c.ral_telefone}</li> : null}
          {c.ral_email ? <li>Email: {c.ral_email}</li> : null}
          {c.ral_area ? <li>Área geográfica: {c.ral_area}</li> : null}
          {c.ral_website ? (
            <li>
              Website:{' '}
              <a className="text-emerald-600 underline" href={c.ral_website} target="_blank" rel="noreferrer">
                {c.ral_website}
              </a>
            </li>
          ) : null}
        </ul>
      ) : (
        <p>
          Em caso de litígio de consumo, pode recorrer às entidades de resolução alternativa de litígios
          competentes ou utilizar o{' '}
          <a className="text-emerald-600 underline" href="https://www.consumidor.gov.pt" target="_blank" rel="noreferrer">
            Portal do Consumidor
          </a>
          .
        </p>
      )}
      {c.ral_texto ? <p>{c.ral_texto}</p> : null}
      <p>
        Livro de Reclamações Eletrónico:{' '}
        <a className="text-emerald-600 underline" href={c.livro_reclamacoes_url || 'https://www.livroreclamacoes.pt/inicio'} target="_blank" rel="noreferrer">
          livroreclamacoes.pt
        </a>
        .
      </p>
    </>
  );
}

function IndependenceNote() {
  return (
    <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
      O Marketplace TVDE é uma plataforma independente e <strong>não é afiliado, patrocinado ou
      representado pela Uber ou pela Bolt</strong>. As marcas, logótipos e designações pertencem aos
      respectivos titulares. Quando indicada, a elegibilidade de uma viatura para determinada
      plataforma é declarada pelo anunciante e está sujeita a confirmação junto do operador TVDE e das
      respectivas plataformas.
    </p>
  );
}

export function TermosCondicoes() {
  const c = useCompany();
  return (
    <Container
      title="Termos e Condições"
      description="Termos e Condições de utilização do Marketplace TVDE: registo, publicação de anúncios, pagamentos, responsabilidade e regras da plataforma."
      path="/termos"
      testId="page-terms"
    >
      <p>
        O presente documento estabelece os Termos e Condições aplicáveis à utilização do Marketplace{' '}
        <strong>{c.brand}</strong> (doravante "Marketplace"), disponibilizado em{' '}
        <strong>https://{DOMAIN}/</strong>, da titularidade de <strong>{c.owner}</strong>.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">1. Objecto</h2>
      <p>
        O Marketplace é uma plataforma online de intermediação que permite a anunciantes publicar
        viaturas para aluguer ou comissão TVDE e a motoristas manifestar interesse em condução. O
        Marketplace <strong>não é parte</strong> nos negócios celebrados entre anunciantes e interessados
        e limita-se a disponibilizar o espaço de anúncio.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">2. Registo e publicação</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Para publicar um anúncio, o anunciante valida o endereço de email através de código de 6 dígitos.</li>
        <li>O anunciante declara ser o legítimo proprietário/representante da viatura ou identidade anunciada.</li>
        <li>Cada publicação está sujeita a moderação. Podemos aprovar, recusar, ocultar ou remover anúncios que violem estes Termos, informando o anunciante do motivo e da possibilidade de pedir revisão. Em situações de fraude ou risco, a intervenção pode ser imediata.</li>
        <li>É proibido publicar conteúdos falsos, ofensivos, ilegais, ou que violem direitos de terceiros.</li>
      </ul>

      <h2 className="mt-6 text-lg font-bold text-slate-900">3. Pagamentos</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>O preço dos anúncios e a sua duração são definidos no painel de administração e exibidos publicamente antes do pagamento.</li>
        <li>Durante períodos promocionais a publicação pode ser gratuita. O Marketplace reserva o direito de iniciar/terminar promoções a qualquer momento.</li>
        <li>Os pagamentos são processados de forma segura via <strong>Stripe</strong> (Cartão, MB WAY, Multibanco). Não armazenamos dados de cartão.</li>
        <li>Reembolsos e o direito de livre resolução são tratados na secção seguinte.</li>
      </ul>

      <h2 className="mt-6 text-lg font-bold text-slate-900">4. Reembolsos e livre resolução</h2>
      <p>
        Se um anúncio pago for recusado pela moderação, o valor é integralmente devolvido. O consumidor
        pode ter direito de livre resolução no prazo legal de 14 dias; caso solicite expressamente o
        início da publicação antes desse prazo, poderá perder esse direito após a prestação integral do
        serviço, nos termos legalmente aplicáveis. As situações de erro técnico ou pagamento duplicado
        são reembolsadas.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">5. Responsabilidade</h2>
      <p>O Marketplace actua apenas como intermediário. <strong>Não nos responsabilizamos</strong> por:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Veracidade da informação prestada pelos anunciantes;</li>
        <li>Qualidade, segurança ou conformidade das viaturas anunciadas;</li>
        <li>Termos comerciais acordados directamente entre as partes;</li>
        <li>Litígios resultantes do contacto entre anunciantes e interessados.</li>
      </ul>
      <p>Recomendamos verificação documental presencial e celebração de contrato escrito entre as partes.</p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">6. Conversas internas do Marketplace</h2>
      <p className="rounded-lg bg-amber-50 p-2 text-xs font-semibold text-amber-800" data-testid="terms-chat-pending-legal">
        Texto em preparação — sujeito a validação pela advogada antes de entrar em produção.
      </p>
      <ul className="list-disc space-y-1 pl-5">
        <li>As mensagens entre interessados e anunciantes são <strong>guardadas no Marketplace</strong> para permitir a continuidade da negociação, segurança e prova em caso de denúncia.</li>
        <li>O acesso é feito sem conta e sem password, através de verificação do email e de ligações seguras pessoais e revogáveis.</li>
        <li>As conversas estão sujeitas a <strong>moderação por regras automáticas internas</strong> (linguagem ofensiva, spam, tentativas de fraude, pedidos de pagamento suspeitos). Nenhum serviço externo de inteligência artificial lê as mensagens.</li>
        <li>O Marketplace pode <strong>consultar o conteúdo</strong> apenas para suporte, investigação de denúncia, prevenção de fraude, cumprimento destes Termos, segurança ou obrigação legal. Cada acesso exige motivo e fica registado em auditoria.</li>
        <li>Podem ser aplicadas medidas de avisos, silenciamento temporário, bloqueio de participante ou encerramento da conversa. As mensagens removidas ficam identificadas como “Mensagem removida pela moderação”.</li>
        <li>É proibido pedir ou partilhar códigos de verificação, enviar links de pagamento, código, HTML ou conteúdo enganoso.</li>
        <li>Não é permitido anexar fotografias, documentos, ficheiros ou áudio nas mensagens nesta fase.</li>
        <li>Os contactos (telefone e email) só são revelados quando o próprio participante escolhe partilhá-los, dentro daquela conversa.</li>
        <li>As conversas são conservadas por um período limitado após o encerramento, salvo quando exista denúncia, litígio ou obrigação legal.</li>
      </ul>
      <p>
        O nome apresentado publicamente é o <strong>nome público</strong> escolhido pelo utilizador. Dentro da conversa é
        mostrado o nome declarado de cada participante. O Marketplace confirma os dados de contacto indicados
        (email e, quando aplicável, telefone), <strong>não confirma a identidade através de documentos</strong>.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">7. Independência</h2>
      <IndependenceNote />

      <h2 className="mt-6 text-lg font-bold text-slate-900">8. Propriedade intelectual</h2>
      <BrandStatus />

      <h2 className="mt-6 text-lg font-bold text-slate-900">9. Resolução de litígios</h2>
      <RALSection />

      <h2 className="mt-6 text-lg font-bold text-slate-900">10. Lei aplicável e foro</h2>
      <p>
        Estes Termos regem-se pela lei portuguesa. Para resolução de quaisquer litígios não resolvidos
        por via extrajudicial, é competente o foro da comarca de Lisboa, sem prejuízo das normas
        imperativas de defesa do consumidor.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">11. Identificação da empresa</h2>
      <CompanyBlock />

      <h2 className="mt-6 text-lg font-bold text-slate-900">12. Contacto</h2>
      <p>Dúvidas: {c.contactEmail}</p>
    </Container>
  );
}

export function PoliticaPrivacidade() {
  const c = useCompany();
  return (
    <Container
      title="Política de Privacidade (RGPD)"
      description="Como o Marketplace TVDE recolhe, utiliza e protege os seus dados pessoais, em cumprimento do RGPD e da Lei 58/2019."
      path="/privacidade"
      testId="page-privacy"
    >
      <p>
        A presente Política descreve como o Marketplace <strong>{c.brand}</strong>, gerido por{' '}
        <strong>{c.owner}</strong>, trata os dados pessoais recolhidos em <strong>{DOMAIN}</strong>, em
        cumprimento do Regulamento (UE) 2016/679 (RGPD) e da Lei 58/2019. Aplica-se exclusivamente à
        utilização deste site de anúncios; não abrange outras plataformas ou aplicações.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">1. Responsável pelo tratamento</h2>
      <CompanyBlock />
      <p>Contacto para questões de privacidade: <strong>{c.privacyEmail}</strong></p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">2. Dados que recolhemos</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li><strong>Anunciante:</strong> nome, email, telefone, cidade, fotografias e descrição da viatura ou perfil de motorista.</li>
        <li><strong>Interessado (mensagem/lead):</strong> nome, email, telefone (opcional), mensagem, data/hora e IP.</li>
        <li><strong>Visitante:</strong> dados técnicos e de análise (apenas se aceitar cookies de análise).</li>
        <li><strong>Pagamentos:</strong> processados directamente pela Stripe; o site nunca armazena dados de cartão.</li>
        <li><strong>Segurança:</strong> registos técnicos e códigos de verificação (email/SMS) para prevenir abuso e spam.</li>
        <li><strong>Conversas internas:</strong> mensagens trocadas dentro do Marketplace, nome declarado, nome público, estado de verificação do email/telefone, registos de moderação, denúncias, sessões de acesso (em forma cifrada/resumo) e registos de auditoria dos acessos administrativos.</li>
      </ul>
      <p>O Marketplace não gere contas com palavra-passe para anunciantes ou interessados: o acesso à gestão do anúncio é feito por código de verificação enviado para o email.</p>
      <p data-testid="privacy-audience-info">A audiência própria, ativada apenas com consentimento de análise, regista visualizações, data/hora, tipo de página e identificadores aleatórios em forma de resumo criptográfico. Não recolhe nomes, emails, IPs, parâmetros de pesquisa ou conteúdo das conversas. O administrador consulta apenas totais agregados. A presença expira após cinco minutos sem sinal; a retirada do consentimento interrompe esta medição.</p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">3. Finalidades e base legal</h2>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-slate-300">
            <th className="py-2 text-left">Finalidade</th>
            <th className="py-2 text-left">Base legal</th>
            <th className="py-2 text-left">Prazo</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-200">
            <td className="py-2">Publicação e moderação de anúncios</td>
            <td>Execução de contrato (Art. 6.º/1/b)</td>
            <td>Até 12 meses após expiração</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="py-2">Entrega de mensagens ao anunciante</td>
            <td>Execução de pré-contrato / interesse legítimo</td>
            <td>6 meses</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="py-2">Verificação por email/SMS e segurança</td>
            <td>Interesse legítimo (prevenção de fraude)</td>
            <td>Até 12 meses</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="py-2">Processamento de pagamentos</td>
            <td>Execução de contrato + obrigação legal</td>
            <td>Prazos fiscais aplicáveis</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="py-2">Cookies de análise</td>
            <td>Consentimento</td>
            <td>13 meses</td>
          </tr>
          <tr className="border-b border-slate-200">
            <td className="py-2">Cookies de publicidade</td>
            <td>Consentimento</td>
            <td>13 meses</td>
          </tr>
          <tr>
            <td className="py-2">Alertas/comunicações promocionais (opcional)</td>
            <td>Consentimento separado</td>
            <td>Até retirar o consentimento</td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs">
        Não utilizamos "consentimento" como base legal quando o tratamento é necessário para publicar o
        anúncio ou entregar uma mensagem solicitada. Newsletters e alertas exigem consentimento
        separado, opcional e nunca previamente selecionado.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">4. Dados obrigatórios e opcionais</h2>
      <p>
        Os campos assinalados como obrigatórios são necessários para publicar o anúncio ou enviar a
        mensagem. O telefone é opcional quando o contacto é feito apenas por email. A exibição pública
        do telefone/email do anunciante é <strong>opcional</strong> e está desligada por defeito.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">5. Destinatários e subcontratantes</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Mensagens de interessados são enviadas ao <strong>anunciante</strong> do respectivo anúncio.</li>
        <li><strong>Stripe</strong> (UE/EUA) — processamento de pagamentos.</li>
        <li><strong>Resend</strong> (EUA) — envio transaccional de emails.</li>
        <li><strong>SMS.to</strong> (Chipre) — envio transaccional de SMS.</li>
        <li><strong>Google</strong> (EUA) — analytics e publicidade, apenas após consentimento.</li>
        <li><strong>PostHog</strong> — análise de produto, apenas após consentimento.</li>
        <li><strong>MongoDB Atlas</strong> (UE) — armazenamento dos dados da plataforma.</li>
      </ul>

      <h2 className="mt-6 text-lg font-bold text-slate-900">6. Transferências internacionais</h2>
      <p>
        Sempre que existam transferências para fora do EEE, estas assentam em salvaguardas adequadas
        (Cláusulas Contratuais-Tipo) exigidas aos respectivos subcontratantes.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">7. Segurança</h2>
      <p>
        Os dados são transmitidos por HTTPS/TLS. O acesso administrativo é restrito e auditável.
        Aplicamos limites de utilização e verificações para prevenir spam e acesso indevido.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">7-A. Conversas internas (mensagens)</h2>
      <p className="rounded-lg bg-amber-50 p-2 text-xs font-semibold text-amber-800" data-testid="privacy-chat-pending-legal">
        Texto em preparação — sujeito a validação pela advogada antes de entrar em produção.
      </p>
      <ul className="list-disc space-y-1 pl-5">
        <li><strong>Armazenamento:</strong> as mensagens ficam guardadas na conversa correspondente ao anúncio.</li>
        <li><strong>Finalidade:</strong> permitir a negociação entre interessado e anunciante, suporte, segurança e prova em caso de denúncia (execução do serviço e interesse legítimo na prevenção de fraude).</li>
        <li><strong>Moderação:</strong> aplicamos regras automáticas internas (palavras ofensivas, spam, phishing, pedidos de pagamento). Não utilizamos serviços externos de inteligência artificial para ler mensagens.</li>
        <li><strong>Consulta administrativa:</strong> apenas com motivo registado (suporte, denúncia, fraude, Termos, segurança ou obrigação legal), com auditoria do administrador, data/hora e conversa consultada.</li>
        <li><strong>Denúncias:</strong> guardamos o motivo, a descrição e o histórico de decisão.</li>
        <li><strong>Conservação:</strong> por definição técnica provisória, 365 dias após o encerramento da conversa; denúncias e registos de auditoria por períodos mais longos. A eliminação automática está <strong>desativada</strong> até validação jurídica; existe simulação e execução manual pelo administrador.</li>
        <li><strong>Bloqueio:</strong> pode bloquear o outro participante; o histórico é preservado e nenhuma informação adicional é revelada.</li>
        <li><strong>Direitos:</strong> mantém todos os direitos previstos no ponto 8, incluindo acesso e eliminação, salvo quando exista denúncia, litígio ou obrigação legal que imponha a conservação.</li>
        <li><strong>Contactos:</strong> o telefone e o email só são revelados na conversa se o próprio participante escolher partilhá-los.</li>
      </ul>

      <h2 className="mt-6 text-lg font-bold text-slate-900">8. Os seus direitos</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Acesso, rectificação e eliminação;</li>
        <li>Portabilidade e limitação;</li>
        <li>Oposição ao tratamento;</li>
        <li>Retirada do consentimento a qualquer momento (incl. cookies, em "Gerir preferências de cookies");</li>
        <li>Reclamação à <a href="https://www.cnpd.pt" target="_blank" rel="noreferrer" className="text-emerald-600 underline">CNPD</a>.</li>
      </ul>
      <p>Para exercer estes direitos: <strong>{c.privacyEmail}</strong> — respondemos em até 30 dias.</p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">9. Cookies</h2>
      <p>
        Utilizamos cookies essenciais e, mediante consentimento, cookies de análise e publicidade.
        Consulte a{' '}
        <Link to="/cookies" className="text-emerald-600 underline">Política de Cookies</Link> e faça a
        sua gestão em qualquer momento.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">10. Menores</h2>
      <p>O Marketplace não se destina a menores de 18 anos.</p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">11. Alterações</h2>
      <p>Esta Política pode ser actualizada. A versão em vigor é sempre a publicada nesta página.</p>
    </Container>
  );
}

export function PoliticaCookies() {
  const c = useCompany();
  return (
    <Container
      title="Política de Cookies"
      description="Que cookies utiliza o Marketplace TVDE (essenciais, análise e publicidade) e como gerir o seu consentimento."
      path="/cookies"
      testId="page-cookies"
    >
      <p>
        Esta página descreve os cookies e tecnologias semelhantes utilizados pelo Marketplace{' '}
        <strong>{c.brand}</strong>. Antes do seu consentimento, apenas são usados cookies essenciais;
        os cookies de análise e publicidade só são ativados depois de os aceitar.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">O que são cookies</h2>
      <p>
        São pequenos ficheiros ou tecnologias de armazenamento (incluindo <em>localStorage</em>) usados
        para o funcionamento do site, lembrar preferências e, com consentimento, medir audiências.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">Cookies e tecnologias utilizados</h2>
      <p className="my-3 text-sm" data-testid="cookies-audience-info">A audiência própria utiliza <code className="break-all">mp_audience_visitor_v1</code> no localStorage, apenas depois de aceitar Análise. É um identificador aleatório do navegador, renovado após 90 dias, para distinguir visitantes e contar visualizações. A aba usa um identificador apenas em memória. Pode retirar a autorização nas preferências de cookies.</p>
      <div className="overflow-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-slate-300">
              <th className="py-2 pr-3 text-left">Nome</th>
              <th className="py-2 pr-3 text-left">Fornecedor</th>
              <th className="py-2 pr-3 text-left">Finalidade</th>
              <th className="py-2 pr-3 text-left">Categoria</th>
              <th className="py-2 pr-3 text-left">Duração</th>
              <th className="py-2 text-left">Quando é instalado</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200">
              <td className="py-2 pr-3">mp_cookie_consent_v2</td>
              <td>Marketplace TVDE</td>
              <td>Guardar a sua escolha de cookies</td>
              <td className="font-semibold text-emerald-600">Essencial</td>
              <td>Persistente (localStorage)</td>
              <td>Ao escolher no banner</td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2 pr-3">Sessão / segurança</td>
              <td>Marketplace TVDE</td>
              <td>Funcionamento, idioma e segurança</td>
              <td className="font-semibold text-emerald-600">Essencial</td>
              <td>Sessão / até 30 dias</td>
              <td>Sempre</td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2 pr-3">__emg_vid, __emg_sid</td>
              <td>Emergent (alojamento)</td>
              <td>Identificador técnico da plataforma de alojamento</td>
              <td className="font-semibold text-emerald-600">Essencial</td>
              <td>Sessão / técnico</td>
              <td>Sempre</td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2 pr-3">_ga, _ga_*</td>
              <td>Google Analytics</td>
              <td>Estatísticas de utilização</td>
              <td className="font-semibold text-amber-600">Análise</td>
              <td>13 meses</td>
              <td>Após aceitar "Análise"</td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2 pr-3">ph_* / PostHog</td>
              <td>PostHog</td>
              <td>Análise de produto</td>
              <td className="font-semibold text-amber-600">Análise</td>
              <td>Até 12 meses</td>
              <td>Após aceitar "Análise"</td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2 pr-3">_gcl_*, conversões</td>
              <td>Google Ads</td>
              <td>Medição de campanhas publicitárias</td>
              <td className="font-semibold text-amber-600">Publicidade</td>
              <td>13 meses</td>
              <td>Após aceitar "Publicidade"</td>
            </tr>
            <tr>
              <td className="py-2 pr-3">__stripe_*</td>
              <td>Stripe</td>
              <td>Segurança e prevenção de fraude no pagamento</td>
              <td className="font-semibold text-emerald-600">Essencial (no pagamento)</td>
              <td>Até 12 meses</td>
              <td>Ao iniciar um pagamento</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        A finalidade de cada cookie é confirmada tecnicamente; nenhum cookie de análise ou publicidade é
        classificado como essencial.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">Gerir o consentimento</h2>
      <p>
        No banner inicial pode <em>Aceitar tudo</em>, escolher <em>Apenas essenciais</em> ou{' '}
        <em>Personalizar</em>. Pode alterar a sua escolha a qualquer momento através do botão{' '}
        <strong>"Gerir preferências de cookies"</strong> no rodapé — com a mesma facilidade com que a deu.
      </p>
    </Container>
  );
}

export function AvisoLegal() {
  const c = useCompany();
  return (
    <Container
      title="Aviso Legal"
      description="Aviso legal do Marketplace TVDE: identificação da empresa, função da plataforma, limitação de responsabilidade e direitos da marca."
      path="/aviso-legal"
      testId="page-legal"
    >
      <h2 className="mt-2 text-lg font-bold text-slate-900">Identificação</h2>
      <CompanyBlock />

      <h2 className="mt-6 text-lg font-bold text-slate-900">Função do Marketplace</h2>
      <p>
        O Marketplace limita-se a fornecer infraestrutura tecnológica para que terceiros (anunciantes e
        interessados) se possam encontrar. <strong>Não somos parte</strong> nas relações comerciais
        celebradas entre eles.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">Limitação de responsabilidade</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Não garantimos a veracidade da informação prestada pelos anunciantes.</li>
        <li>Não somos responsáveis por prejuízos resultantes de negócios entre as partes.</li>
        <li>Recomendamos verificar sempre documentação original (DUA, IUC, seguros, inspeção) antes de qualquer transacção.</li>
        <li>Em caso de suspeita de fraude, contacte-nos para suspensão do anúncio.</li>
      </ul>

      <h2 className="mt-6 text-lg font-bold text-slate-900">Independência</h2>
      <IndependenceNote />

      <h2 className="mt-6 text-lg font-bold text-slate-900">Direitos da marca</h2>
      <BrandStatus />

      <h2 className="mt-6 text-lg font-bold text-slate-900">Hiperligações para terceiros</h2>
      <p>
        O site pode conter links para websites de terceiros (Stripe, etc.). Não nos responsabilizamos
        pelo conteúdo, políticas ou práticas desses sites.
      </p>

      <h2 className="mt-6 text-lg font-bold text-slate-900">Resolução alternativa de litígios</h2>
      <RALSection />
    </Container>
  );
}
